import * as React from "react";
import { forwardRef, useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { DEFAULT_CUSTOM_HIGHLIGHT_RANGE } from "views/Constants";
import { ConditionLabelMap } from "views/logic/views/formatting/highlighting/HighlightingRule";
import { ColorPickerPopover, IconButton } from "components/common";
import HighlightForm from "views/components/sidebar/highlighting/HighlightForm";
import { StaticColor } from "views/logic/views/formatting/highlighting/HighlightingColor";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import ColorPreview from "./ColorPreview";
export const Container = styled.div(
  ({ theme, $displayBorder = true }) => css`
    display: flex;
    padding-top: 5px;
    padding-bottom: 5px;
    word-break: break-word;

    &:not(:last-child) {
      border-bottom: ${$displayBorder ? `1px solid ${theme.colors.global.background}` : "none"};
    }
  `
);
const RightCol = styled.div`
  flex: 1;
`;
const ButtonContainer = styled.div`
  display: inline-flex;
  float: right;
`;
export const RuleContainer = styled.div`
  padding-top: 2px;
  display: inline-block;
`;
const RuleColorPreview = ({ color, onChange }) => {
  const _onChange = useCallback(
    (newColor, _ignored, hidePopover) => onChange(StaticColor.create(newColor), hidePopover),
    [onChange]
  );
  if (color.isStatic()) {
    return /* @__PURE__ */ React.createElement(
      ColorPickerPopover,
      {
        id: "formatting-rule-color",
        placement: "right",
        color: color.color,
        colors: DEFAULT_CUSTOM_HIGHLIGHT_RANGE.map((c) => [c]),
        triggerNode: /* @__PURE__ */ React.createElement(ColorPreview, { color }),
        onChange: _onChange
      }
    );
  }
  if (color.isGradient()) {
    return /* @__PURE__ */ React.createElement(ColorPreview, { color });
  }
  throw new Error(`Invalid highlighting color: ${color}`);
};
const HighlightingRule = ({ rule, className = void 0, dragHandle = void 0, onUpdate, onDelete }, ref) => {
  const { field, value, color, condition } = rule;
  const [showForm, setShowForm] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const _onChange = useCallback(
    (newColor, hidePopover) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_SIDEBAR_HIGHLIGHT_UPDATED, {
        app_action_value: "search-sidebar-highlight-color-update"
      });
      return onUpdate(rule, rule.field, rule.value, rule.condition, newColor).then(hidePopover);
    },
    [onUpdate, rule, sendTelemetry]
  );
  const _onDelete = useCallback(() => {
    if (window.confirm("Do you really want to remove this highlighting?")) {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_SIDEBAR_HIGHLIGHT_DELETED, {
        app_action_value: "search-sidebar-highlight-delete"
      });
      return onDelete(rule);
    }
    return Promise.resolve();
  }, [onDelete, rule, sendTelemetry]);
  return /* @__PURE__ */ React.createElement(Container, { className, ref }, /* @__PURE__ */ React.createElement(RuleColorPreview, { color, onChange: _onChange }), /* @__PURE__ */ React.createElement(RightCol, null, /* @__PURE__ */ React.createElement(RuleContainer, { "data-testid": "highlighting-rule" }, /* @__PURE__ */ React.createElement("strong", null, field), " ", ConditionLabelMap[condition], " ", /* @__PURE__ */ React.createElement("i", null, '"', String(value), '"'), "."), /* @__PURE__ */ React.createElement(ButtonContainer, null, /* @__PURE__ */ React.createElement(IconButton, { title: "Edit this Highlighting Rule", name: "edit_square", onClick: () => setShowForm(true) }), /* @__PURE__ */ React.createElement(IconButton, { title: "Remove this Highlighting Rule", name: "delete", onClick: _onDelete }), dragHandle)), showForm && /* @__PURE__ */ React.createElement(
    HighlightForm,
    {
      onClose: () => setShowForm(false),
      rule,
      onSubmit: (newField, newValue, newCondition, newColor) => onUpdate(rule, newField, newValue, newCondition, newColor)
    }
  ));
};
export default forwardRef(HighlightingRule);
